import { OverlapDetails } from './priorityCalculator'
import { resolveOneTimeAgainstRecurringSeries } from './recurringConflictResolver'
import { toLocalISOString } from './shared'
import { CalendarEvent } from '../context/Data'

type OverlapMutations = {
	updateEvent: (eventId: string, updates: Partial<CalendarEvent>) => void
	addEvent: (event: CalendarEvent) => void
	deleteEvent: (eventId: string) => void
}

type ApplyOverlapResolutionInput = OverlapMutations & {
	details: OverlapDetails
	newSlotIsRecurring: boolean
	// Optional new slot bounds. When provided, we can correct known classifier edge-cases
	// for recurring series by projecting new slot times onto the conflict day.
	newSlotStart?: Date
	newSlotEnd?: Date
}

function getSafeScenario(
	details: OverlapDetails,
	newSlotStart?: Date,
	newSlotEnd?: Date
): OverlapDetails['scenario'] {
	const {
		existingEvent,
		existingStart,
		existingEnd,
		scenario: rawScenario,
	} = details
	if (!existingEvent.rrule) return rawScenario
	if (rawScenario !== 'new_covers_existing') return rawScenario
	if (!newSlotStart || !newSlotEnd) return rawScenario

	const ref = new Date(existingStart)
	ref.setHours(0, 0, 0, 0)

	const projectedNewStart = new Date(ref)
	projectedNewStart.setHours(
		newSlotStart.getHours(),
		newSlotStart.getMinutes(),
		0,
		0
	)
	const projectedNewEnd = new Date(ref)
	projectedNewEnd.setHours(newSlotEnd.getHours(), newSlotEnd.getMinutes(), 0, 0)
	if (projectedNewEnd <= projectedNewStart) {
		projectedNewEnd.setDate(projectedNewEnd.getDate() + 1)
	}

	const covers =
		projectedNewStart.getTime() <= existingStart.getTime() &&
		projectedNewEnd.getTime() >= existingEnd.getTime()

	return covers ? rawScenario : 'new_inside_existing'
}

export function applyOverlapResolution({
	details,
	newSlotIsRecurring,
	newSlotStart,
	newSlotEnd,
	updateEvent,
	addEvent,
	deleteEvent,
}: ApplyOverlapResolutionInput) {
	const safeScenario = getSafeScenario(details, newSlotStart, newSlotEnd)
	const normalizedDetails =
		safeScenario === details.scenario
			? details
			: { ...details, scenario: safeScenario }

	const {
		existingEvent,
		existingEnd,
		existingStart,
		newStart,
		newEnd,
		scenario,
	} = normalizedDetails

	const recurringOps = resolveOneTimeAgainstRecurringSeries(
		normalizedDetails,
		newSlotIsRecurring
	)
	if (recurringOps) {
		if (recurringOps.deleteExisting) {
			deleteEvent(existingEvent.id)
		}
		if (recurringOps.updateExisting) {
			updateEvent(existingEvent.id, recurringOps.updateExisting)
		}
		for (const eventToAdd of recurringOps.addEvents || []) {
			addEvent(eventToAdd)
		}
		return
	}

	if (scenario === 'new_covers_existing') {
		deleteEvent(existingEvent.id)
		return
	}

	if (scenario === 'new_inside_existing') {
		const firstPartDuration = newStart.getTime() - existingStart.getTime()
		const remainderDuration = existingEnd.getTime() - newEnd.getTime()

		updateEvent(existingEvent.id, {
			end: newStart,
			...(existingEvent.rrule
				? {
						duration: { milliseconds: firstPartDuration },
					}
				: {}),
		})

		if (remainderDuration > 0) {
			const remainderEvent: CalendarEvent = {
				...existingEvent,
				id: `${existingEvent.id}-remainder-${Date.now()}`,
				start: newEnd,
				end: existingEnd,
				timeslotId: undefined,
				timeslotContentId: undefined,
				...(existingEvent.rrule
					? {
							rrule: {
								...existingEvent.rrule,
								dtstart: toLocalISOString(newEnd),
							},
							startTime: `${String(newEnd.getHours()).padStart(2, '0')}:${String(newEnd.getMinutes()).padStart(2, '0')}:00`,
							duration: { milliseconds: remainderDuration },
						}
					: {}),
			}
			addEvent(remainderEvent)
		}
		return
	}

	if (scenario === 'new_overlaps_start') {
		const newAffectedStart = newEnd
		const newDuration = existingEnd.getTime() - newAffectedStart.getTime()
		updateEvent(existingEvent.id, {
			start: newAffectedStart,
			...(existingEvent.rrule
				? {
						rrule: {
							...existingEvent.rrule,
							dtstart: toLocalISOString(newAffectedStart),
						},
						startTime: `${String(newAffectedStart.getHours()).padStart(2, '0')}:${String(newAffectedStart.getMinutes()).padStart(2, '0')}:00`,
						duration: { milliseconds: newDuration },
					}
				: {}),
		})
		return
	}

	if (scenario === 'new_overlaps_end') {
		const newAffectedEnd = newStart
		const newDuration = newAffectedEnd.getTime() - existingStart.getTime()
		updateEvent(existingEvent.id, {
			end: newAffectedEnd,
			...(existingEvent.rrule
				? {
						duration: { milliseconds: newDuration },
					}
				: {}),
		})
	}
}
